import {
  CallingReportResponseSchema,
  CallsenseCallDetailSchema,
  CallsenseCallRowSchema,
  CallsenseCallsResponseSchema,
  CallsenseOverviewResponseSchema,
  type CallingReportResponse,
  type CallsenseCallDetail,
  type CallsenseCallRow,
  type CallsenseCallsResponse,
  type CallsenseOverviewResponse,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

export interface CallsenseFilters {
  from?: string;
  to?: string;
  agent?: string;
  /** all (default) / cd / wa */
  channel?: 'all' | 'cd' | 'wa';
}

export interface CallsenseCallsFilters extends CallsenseFilters {
  outcome?: string;
  sentiment?: string;
  direction?: string;
  language?: string;
  q?: string;
  minScore?: number;
  maxScore?: number;
  flaggedOnly?: boolean;
  followUpOnly?: boolean;
  scoredOnly?: boolean;
  page: number;
  pageSize: number;
}

const clean = (params: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false),
  );

export const getCallsenseOverview = async (
  filters: CallsenseFilters,
): Promise<CallsenseOverviewResponse> => {
  const { data } = await apiClient.get<unknown>('/callsense/overview', {
    params: clean({ ...filters }),
  });
  return CallsenseOverviewResponseSchema.parse(data);
};

export const getCallsenseCallingReport = async (
  filters: CallsenseFilters,
): Promise<CallingReportResponse> => {
  const { data } = await apiClient.get<unknown>('/callsense/calling-report', {
    params: clean({ from: filters.from, to: filters.to, channel: filters.channel }),
  });
  return CallingReportResponseSchema.parse(data);
};

export const getCallsenseCalls = async (
  filters: CallsenseCallsFilters,
): Promise<CallsenseCallsResponse> => {
  const { data } = await apiClient.get<unknown>('/callsense/calls', {
    params: clean({ ...filters }),
  });
  return CallsenseCallsResponseSchema.parse(data);
};

/**
 * Full analysis record. With `leadId`, uses the lead-context route
 * (`/leads/:id/call-insights/:insightId`) which is gated like the lead call
 * history (ADMIN + SALES_LEAD) instead of the CallSense dashboard permission.
 */
export const getCallsenseCall = async (
  id: string,
  leadId?: string,
): Promise<CallsenseCallDetail> => {
  const url = leadId ? `/leads/${leadId}/call-insights/${id}` : `/callsense/calls/${id}`;
  const { data } = await apiClient.get<unknown>(url);
  return CallsenseCallDetailSchema.parse(data);
};

/**
 * Recording bytes via the authenticated proxy (needed for WhatsApp/DoubleTick
 * recordings that 403 on a raw `<audio src>`). Returns an object URL to feed
 * the player; the caller must revoke it. CallerDesk plays fine from the raw
 * URL, so only use this when the raw URL is not directly playable.
 */
export const getCallsenseRecordingObjectUrl = async (id: string): Promise<string> => {
  const { data } = await apiClient.get<Blob>(`/callsense/calls/${id}/recording`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
};

const LeadCallInsightsResponseSchema = z.object({
  insights: z.record(z.string(), CallsenseCallRowSchema),
});

/** CallSense rows for a lead's CallerDesk sids, keyed by sid. */
export const getLeadCallInsights = async (
  leadId: string,
  sids: string[],
): Promise<Record<string, CallsenseCallRow>> => {
  const { data } = await apiClient.get<unknown>(`/leads/${leadId}/call-insights`, {
    params: { sids: sids.join(',') },
  });
  return LeadCallInsightsResponseSchema.parse(data).insights;
};
